
import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown } from "lucide-react";
import { FlameMark } from "@/components/Logo";
import { useLang } from "@/i18n/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1555086156-e6c7353d283f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxzdHlsaXNoJTIwbmlnaHRsaWZlJTIwcGFydHklMjBkYXJrfGVufDB8fHx8MTc5MTEyNjc2OXww&ixlib=rb-4.1.0&q=85";

const LINE_STYLES = [
  "text-white",
  "text-flame",
  "text-outline",
];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "22%"]
  );

  const fade = useTransform(
    scrollYProgress,
    [0, 0.75],
    [1, 0]
  );

  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const orbX = useSpring(mx, {
    stiffness: 55,
    damping: 18,
  });

  const orbY = useSpring(my, {
    stiffness: 55,
    damping: 18,
  });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();

    if (!r) return;

    mx.set(
      ((e.clientX - r.left) / r.width - 0.5) * 60
    );

    my.set(
      ((e.clientY - r.top) / r.height - 0.5) * 40
    );
  };

  const lines = [
    "hero.line1",
    "hero.line2",
    "hero.line3",
  ];

  return (
    <section
      ref={ref}
      data-testid="hero"
      onMouseMove={onMouseMove}
      className="relative flex min-h-0 flex-col justify-start overflow-hidden px-5 pb-10 pt-24 md:min-h-screen md:justify-end md:px-10 md:pb-20 md:pt-44"
    >
      {/* Background image */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 -z-10"
      >
        <img
          src={HERO_IMG}
          alt="Crowd at a KNDLA night"
          className="h-[125%] w-full object-cover opacity-35"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-coal via-coal/55 to-coal/25" />
      </motion.div>

      {/* Background lighting */}
      <motion.div
        style={{ x: orbX, y: orbY }}
        className="pointer-events-none absolute -left-32 top-1/4 -z-10 h-[28rem] w-[28rem] rounded-full bg-flame/25 blur-[130px]"
      />

      <motion.div
        style={{ x: orbY, y: orbX }}
        className="pointer-events-none absolute -right-24 bottom-0 -z-10 h-[24rem] w-[24rem] rounded-full bg-ember/20 blur-[120px]"
      />

      {/* Animated mascot - desktop */}
      <motion.div
        initial={{
          opacity: 0,
          scale: 0.85,
          y: 30,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
        }}
        transition={{
          duration: 1,
          delay: 1.15,
          ease: EASE,
        }}
        className="hero-desktop-mascot pointer-events-none absolute right-4 top-20 z-0 hidden md:right-10 md:block"
      >
        <motion.img
          src="/mascot-ghost.png"
          alt="KNDLA candle ghost mascot"
          data-testid="hero-mascot"
          animate={{
            y: [0, -16, 0],
            rotate: [0, 1.5, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="w-28 drop-shadow-[0_0_50px_rgba(255,69,0,0.35)] lg:w-44"
        />
      </motion.div>

      {/* Mobile slogan */}
      <div className="relative z-10 mb-5 flex justify-center sm:hidden">
        <motion.p
          initial={{
            opacity: 0,
            y: 16,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.7,
            delay: 0.15,
          }}
          className="flex max-w-full items-center justify-center gap-2 text-center text-[9px] font-bold uppercase leading-snug tracking-[0.12em] text-white/60"
        >
          <FlameMark className="h-4 w-4 shrink-0 animate-flicker text-flame" />
          {t("hero.eyebrow")}
        </motion.p>
      </div>

      {/* Main headline with animated mascot */}
      <div className="relative z-10">
        <h1 className="relative z-10 font-syne font-extrabold leading-[0.92] tracking-tighter">
          {lines.map((key, i) => (
            <span
              key={key}
              className="block overflow-hidden pb-1"
            >
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.95,
                  delay: 0.3 + i * 0.15,
                  ease: EASE,
                }}
                className={`block text-[clamp(2.6rem,9vw,8.75rem)] ${LINE_STYLES[i]}`}
              >
                {t(key)}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Floating mascot beside headline on mobile */}
        <motion.img
          src="/mascot-ghost.png"
          alt="KNDLA candle ghost mascot"
          data-testid="hero-mascot-mobile"
          animate={{
            y: [0, -8, 0],
            rotate: [0, 1.5, 0],
          }}
          transition={{
            duration: 5.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="hero-inline-mascot pointer-events-none absolute right-1 top-[5.1rem] z-20 h-16 w-16 object-contain md:hidden"
        />
      </div>

      {/* Description and buttons */}
      <motion.div
        initial={{
          opacity: 0,
          y: 24,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.8,
          delay: 0.95,
          ease: EASE,
        }}
        className="relative z-10 mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <p className="max-w-md text-base leading-relaxed text-white/65 md:text-lg">
          {t("hero.sub")}
        </p>

        <div className="flex flex-wrap items-center gap-4">
          <a
            data-testid="hero-parties-btn"
            href="#parties"
            className="flame-glow rounded-full bg-flame px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:bg-ember"
          >
            {t("hero.ctaParties")}
          </a>

          <a
            data-testid="hero-clubs-btn"
            href="#clubs"
            className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:border-white hover:bg-white/5"
          >
            {t("hero.ctaClubs")}
          </a>
        </div>
      </motion.div>

      {/* Scroll indicator - desktop */}
      <motion.div
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: 1.6,
          duration: 0.8,
        }}
        className="pointer-events-none absolute bottom-5 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-white/35 md:flex"
      >
        {t("hero.scroll")}
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
      </motion.div>
    </section>
  );
}
